import { buildContributorKey } from "helpers/contributors"
import { IsoDate } from "helpers/types/dates"
import { camelCaseToSpace } from "helpers/string"
import { generateClassificationQueryForContributorDescriptors, classificationsMap } from "helpers/classification"
import { makeUniverseFilter } from "../helpers"

interface ClassificationTier {
  id: string
  level: number
  name: string
}
interface ClassificationTierGroup {
  id: string
  tiers: ClassificationTier[]
}

function generateAllClassificationQueryParts(classificationTiers: ClassificationTierGroup[]) {
  return classificationTiers
    .map((classification) => {
      const tiers = classification.tiers?.map(tier => tier.id)
      return generateClassificationQueryForContributorDescriptors(classification.id, classificationTiers, tiers)
    })
    .join("\n")
}

function getDescriptorsQuery(classificationTiers: ClassificationTierGroup[]) {
  return `query ($modelId: String!, $filter: [SecuritySearchFilter!]!, $on: Date!, $skip: Int!, $sort: [SecuritySearchSort], $currency: Currency) {
    model (id: $modelId) {
      securitySearch (filter: $filter, on: $on, skip: $skip, sort: $sort, currency: $currency) {
        securities {
          id
          sedol
          figi
          bloombergTicker
          assetClass
          assetSubclass
          ticker
          mic
          description
          averageDailyVolume
          marketCapitalization
          country
          currency
          modelProviderId
          ${generateAllClassificationQueryParts(classificationTiers)}
        }
      }
    }
  }`
}

export interface SecuritySearchPositionSet {
  id: string
  type: string
}

interface CreateDescriptorsContributorsQueryInput {
  prefix: string
  modelId: string
  positionSet: SecuritySearchPositionSet | undefined
  base?: SecuritySearchPositionSet | undefined
  to: IsoDate
  currency: string
  classifications?: unknown
  count?: number
}

interface ClassificationValue { name: string, id: string }

interface Security {
  id: string
  sedol: string | null
  figi: string | null
  bloombergTicker: string | null
  assetClass: string | null
  assetSubclass: string | null
  ticker: string | null
  mic: string | null
  description: string | null
  averageDailyVolume: number | null
  marketCapitalization: number | null
  country: string | null
  currency: string | null
  modelProviderId: string | null
  [key: string]: string | number | null | ClassificationValue
}

interface DataResponse {
  data: {
    model: {
      securitySearch: {
        securities: Security[]
      }
    }
  }
}

interface Contributor {
  values: Record<string, Security>
}

function isClassificationValue(v: string | number | null | ClassificationValue): v is ClassificationValue {
  return !!v
    && typeof v === "object"
    && "name" in v
    && "id" in v
    && typeof v.name === "string"
    && typeof v.id === "string"
}

function flattenClassificationValues(security: Security & Record<string, unknown>) {
  const result: Record<string, string> = {}

  for (const [key, value] of Object.entries(security)) {
    if (isClassificationValue(value)) {
      result[key] = value.name
    }
  }

  return {
    ...security,
    ...result,
  }
}

function parseContributors(prefix: string, securities: Security[]) {
  if (!securities) {
    return {}
  }
  return securities?.reduce((acc: Record<string, Contributor>, security) => {
    acc[buildContributorKey({ ...security, identifier: security.id })] = {
      values: {
        [prefix]: {
          ...flattenClassificationValues(security),
          assetClass: camelCaseToSpace(security.assetClass),
          assetSubclass: camelCaseToSpace(security.assetSubclass),
        },
      },
    }
    return acc
  }, {})
}

export function createDescriptorsContributorsQuery({ prefix, modelId, positionSet, base, to, currency, count, classifications }: CreateDescriptorsContributorsQueryInput) {
  const filter = makeUniverseFilter(positionSet, base)

  if (count === undefined) {
    return []
  }

  const classificationTiers = classificationsMap(classifications, to)

  function makeQuery(skip: number | null) {
    const additionalKeyItems = skip === null ? [] : [skip]
    return {
      prefix,
      key: ["descriptors", "securities", prefix, modelId, positionSet, base, to, classificationTiers, ...additionalKeyItems],
      query: getDescriptorsQuery(classificationTiers),
      variables: {
        modelId,
        filter,
        on: to,
        skip: skip ?? 0,
        currency,
      },
      select: (innerData: DataResponse) => {
        const securities = innerData.data.model.securitySearch.securities
        if (!securities) {
          return {}
        }

        const children = parseContributors(prefix, securities)

        return {
          label: "Total",
          values: {
            [prefix]: {
              identifier: "Total",
            },
          },
          children,
        }
      },
    }
  }

  const descriptorsQueries = []
  let skip = 0
  while (skip < count) {
    const newQuery = makeQuery(skip)
    descriptorsQueries.push(newQuery)
    skip += 200
  }

  return descriptorsQueries
}
